import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import StoryCard from '../common/StoryCard';
import { LanguageContext } from '../../App';

const translations = {
  en: {
    title: 'Success Stories',
    subtitle: 'Stories of positive change through our help',
    readMore: 'Read More Stories'
  },
  hi: {
    title: 'सफलता की कहानियां',
    subtitle: 'हमारी मदद से जिन्होंने अपनी जिंदगी में सकारात्मक बदलाव लाया',
    readMore: 'और कहानियां पढ़ें'
  }
};

interface Story {
  id: string;
  name: string;
  story: string;
  image: string;
  category: string;
  date: string;
  fullStory: string;
}

interface Stories {
  [key: string]: Story[];
}

export const stories: Stories = {
  en: [
    {
      id: 'roshni-journey',
      name: "Roshni's Journey",
      story: 'Born in poverty, Roshni is now a successful doctor. Our educational support gave wings to her dreams.',
      image: '/images/roshni_image.jpg',
      category: 'Education',
      date: 'March 15, 2024',
      fullStory: `In a small village on the outskirts of Delhi, we met Roshni, a bright young student who dreamed of becoming a doctor. Despite her excellent academic performance, financial constraints threatened to end her education after high school.

      Born into a family where her father worked as a daily wage laborer and her mother took up small tailoring jobs, Roshni's dreams seemed distant. However, her determination caught our attention during one of our educational outreach programs.

      Through our comprehensive support program, we provided:
      • Full scholarship for medical entrance exam coaching
      • Financial assistance for MBBS education
      • Monthly stipend for books and study materials
      • Mentorship from practicing doctors
      • Regular counseling and guidance sessions

      Today, Roshni is in her final year of MBBS at a prestigious medical college. She has consistently been among the top performers in her class and has already started giving back to the community by conducting free health camps in her village during holidays.

      "Education is not just about personal growth; it's about lifting up your entire community," says Roshni. "I want to ensure that no other girl's dreams are limited by financial constraints."

      Her success has inspired many other girls in her village to pursue higher education. Roshni's father now proudly says, "My daughter has shown that with proper support and determination, any dream can become reality."

      This is just one example of how your support helps us create lasting change in people's lives. Together, we can help many more students like Roshni achieve their dreams.`
    },
    {
      id: 'asha-devi-independence',
      name: "Asha Devi's Path to Independence",
      story: 'Starting with sewing training, Asha Devi now employs 10 women in her business.',
      image: '/images/asha_image.png',
      category: 'Women Empowerment',
      date: 'March 10, 2024',
      fullStory: `Asha Devi's journey from a homemaker to a successful entrepreneur is a testament to the power of skill development and determination. Living in a small town in Uttar Pradesh, she had always wanted to contribute to her family's income but lacked the necessary skills and resources.

      When she joined our women's empowerment program, we saw in her a natural leader with entrepreneurial spirit. Through our program, we provided:
      • Three months of intensive tailoring training
      • Basic business management education
      • Micro-loan for purchasing sewing machines
      • Marketing and customer service training
      • Ongoing mentorship and support

      Starting with just one sewing machine in her home, Asha Devi began by stitching clothes for her neighbors. As word of her quality work spread, the demand grew. She reinvested her earnings and gradually expanded her business.

      Today, she runs "Asha Silai Kendra," a thriving tailoring center that employs ten women from her community. Her business not only provides tailoring services but also offers training to other women who want to learn the trade.

      "Financial independence gives you confidence and respect in society," Asha Devi shares. "But the real joy comes from being able to help other women become self-reliant."

      Her success has created a ripple effect in her community:
      • Ten families now have additional income sources
      • Twenty more women have learned tailoring skills
      • Local girls have a role model to look up to
      • The community's attitude towards working women has positively changed

      Asha Devi's story shows how skill development combined with proper support can transform not just one life but an entire community.`
    },
    {
      id: 'village-transformation',
      name: 'Transforming Village Life',
      story: 'Clean drinking water and sanitation facilities reduced diseases and improved education in the village.',
      image: '/images/tranforming_image.jpg',
      category: 'Community Development',
      date: 'March 5, 2024',
      fullStory: `The transformation of Rampur village stands as a shining example of how focused community development can create lasting positive change. Three years ago, this village of 200 households faced multiple challenges: contaminated drinking water, lack of proper sanitation, and high dropout rates in schools.

      Our integrated development approach focused on three key areas:
      1. Water Infrastructure
         • Installation of water purification system
         • Construction of water storage tanks
         • Regular water quality testing program
         • Training local youth in system maintenance

      2. Sanitation Facilities
         • Construction of 150 household toilets
         • Community awareness programs on hygiene
         • Waste management system implementation
         • Regular cleanliness drives

      3. Education Support
         • Renovation of primary school building
         • Installation of clean drinking water in school
         • Separate toilet facilities for boys and girls
         • Teacher training programs

      The results have been remarkable:
      • 70% reduction in waterborne diseases
      • 100% school enrollment of children aged 6-14
      • Zero dropout rate in primary school
      • 90% reduction in open defecation
      • Improved attendance, especially among girl students

      Village head Ramesh Kumar shares, "The change is visible in every aspect of our lives. Our children are healthier and regularly attending school. Women don't have to walk kilometers for water anymore."

      The success of this project has made Rampur a model village, with neighboring villages now requesting similar interventions. This transformation shows how addressing basic infrastructure needs can lead to improvements in health, education, and overall quality of life.

      The project's sustainability is ensured through:
      • Local committee for maintenance
      • Regular monitoring and support
      • Community ownership of facilities
      • Ongoing education and awareness programs

      This is a testament to what can be achieved when communities and development organizations work together with a clear vision and sustained effort.`
    }
  ],
  hi: [
    {
      id: 'roshni-journey',
      name: 'रोशनी की कहानी',
      story: 'गरीबी में जन्मी रोशनी आज एक सफल डॉक्टर है। हमारी शिक्षा सहायता ने उसके सपनों को पंख दिए।',
     image: '/images/roshni_image.jpg',
      category: 'शिक्षा',
      date: '15 मार्च 2024',
      fullStory: `दिल्ली के बाहरी इलाके के एक छोटे से गांव में, हमारी मुलाकात रोशनी से हुई, एक होनहार छात्रा जो डॉक्टर बनना चाहती थी। उत्कृष्ट शैक्षणिक प्रदर्शन के बावजूद, आर्थिक कठिनाइयों के कारण उसकी शिक्षा हाई स्कूल के बाद समाप्त होने का खतरा था।

      एक ऐसे परिवार में जन्मी, जहां उसके पिता दैनिक मजदूर के रूप में काम करते थे और मां छोटे-मोटे सिलाई के काम करती थीं, रोशनी के सपने दूर लगते थे। हालांकि, हमारे एक शैक्षिक आउटरीच कार्यक्रम के दौरान उसके दृढ़ संकल्प ने हमारा ध्यान आकर्षित किया।

      हमारे व्यापक सहायता कार्यक्रम के माध्यम से, हमने प्रदान किया:
      • मेडिकल प्रवेश परीक्षा कोचिंग के लिए पूर्ण छात्रवृत्ति
      • एमबीबीएस शिक्षा के लिए वित्तीय सहायता
      • किताबों और अध्ययन सामग्री के लिए मासिक वजीफा
      • कार्यरत डॉक्टरों से मार्गदर्शन
      • नियमित परामर्श और मार्गदर्शन सत्र

      आज, रोशनी एक प्रतिष्ठित मेडिकल कॉलेज में एमबीबीएस के अंतिम वर्ष में है। वह लगातार अपनी कक्षा में शीर्ष प्रदर्शन करने वालों में रही है और छुट्टियों के दौरान अपने गांव में मुफ्त स्वास्थ्य शिविर आयोजित कर समुदाय को वापस देना शुरू कर दिया है।

      "शिक्षा सिर्फ व्यक्तिगत विकास के बारे में नहीं है; यह पूरे समुदाय को ऊपर उठाने के बारे में है," रोशनी कहती है। "मैं यह सुनिश्चित करना चाहती हूं कि किसी अन्य लड़की के सपने वित्तीय बाधाओं से सीमित न हों।"

      उसकी सफलता ने उसके गांव की कई अन्य लड़कियों को उच्च शिक्षा का पीछा करने के लिए प्रेरित किया है। रोशनी के पिता अब गर्व से कहते हैं, "मेरी बेटी ने दिखाया है कि उचित समर्थन और दृढ़ संकल्प के साथ कोई भी सपना हकीकत बन सकता है।"

      यह एक उदाहरण है कि कैसे आपका समर्थन लोगों के जीवन में स्थायी परिवर्तन लाने में हमारी मदद करता है। साथ मिलकर, हम रोशनी जैसे कई और छात्रों को उनके सपने साकार करने में मदद कर सकते हैं।`
    },
    {
      id: 'asha-devi-independence',
      name: 'आशा देवी का आत्मनिर्भर सफर',
      story: 'सिलाई प्रशिक्षण से शुरू हुआ आशा देवी का सफर, आज वह 10 महिलाओं को रोजगार दे रही हैं।',
      image: '/images/asha_image.png',
      category: 'महिला सशक्तिकरण',
      date: '10 मार्च 2024',
      fullStory: `आशा देवी की यात्रा एक गृहिणी से एक सफल उद्यमी बनने तक, कौशल विकास और दृढ़ संकल्प की शक्ति का प्रमाण है। उत्तर प्रदेश के एक छोटे शहर में रहने वाली आशा देवी हमेशा से अपने परिवार की आय में योगदान करना चाहती थीं, लेकिन उनके पास आवश्यक कौशल और संसाधनों की कमी थी।

      जब वह हमारे महिला सशक्तिकरण कार्यक्रम में शामिल हुईं, तो हमने उनमें उद्यमी भावना के साथ एक प्राकृतिक नेता देखा। हमारे कार्यक्रम के माध्यम से, हमने प्रदान किया:
      • तीन महीने का गहन सिलाई प्रशिक्षण
      • बुनियादी व्यवसाय प्रबंधन शिक्षा
      • सिलाई मशीनें खरीदने के लिए माइक्रो-लोन
      • मार्केटिंग और ग्राहक सेवा प्रशिक्षण
      • निरंतर मार्गदर्शन और सहायता

      अपने घर में केवल एक सिलाई मशीन से शुरुआत करते हुए, आशा देवी ने अपने पड़ोसियों के लिए कपड़े सिलना शुरू किया। जैसे-जैसे उनके गुणवत्तापूर्ण काम की चर्चा फैली, मांग बढ़ती गई। उन्होंने अपनी कमाई का पुनर्निवेश किया और धीरे-धीरे अपना व्यवसाय बढ़ाया।

      आज, वह "आशा सिलाई केंद्र" चलाती हैं, एक समृद्ध सिलाई केंद्र जो उनके समुदाय की दस महिलाओं को रोजगार देता है। उनका व्यवसाय न केवल सिलाई सेवाएं प्रदान करता है बल्कि अन्य महिलाओं को भी प्रशिक्षण देता है जो व्यापार सीखना चाहती हैं।

      "वित्तीय स्वतंत्रता आपको समाज में आत्मविश्वास और सम्मान देती है," आशा देवी बताती हैं। "लेकिन वास्तविक खुशी अन्य महिलाओं को आत्मनिर्भर बनने में मदद करने से आती है।"

      उनकी सफलता ने उनके समुदाय में एक लहर पैदा कर दी है:
      • दस परिवारों के पास अब अतिरिक्त आय के स्रोत हैं
      • बीस और महिलाओं ने सिलाई कौशल सीखा है
      • स्थानीय लड़कियों के पास एक रोल मॉडल है
      • कामकाजी महिलाओं के प्रति समुदाय का दृष्टिकोण सकारात्मक रूप से बदला है

      आशा देवी की कहानी दिखाती है कि कैसे उचित समर्थन के साथ कौशल विकास न केवल एक जीवन बल्कि पूरे समुदाय को बदल सकता है।`
    },
    {
      id: 'village-transformation',
      name: 'गाँव की बदलती तस्वीर',
      story: 'स्वच्छ पेयजल और शौचालय निर्माण से गाँव में बीमारियां कम हुईं, बच्चों की शिक्षा बढ़ी।',
      image: '/images/tranforming_image.jpg',
      category: 'समुदाय विकास',
      date: '5 मार्च 2024',
      fullStory: `रामपुर गांव का परिवर्तन इस बात का एक ज्वलंत उदाहरण है कि कैसे केंद्रित सामुदायिक विकास स्थायी सकारात्मक बदलाव ला सकता है। तीन साल पहले, 200 परिवारों के इस गांव को कई चुनौतियों का सामना करना पड़ा: दूषित पेयजल, उचित स्वच्छता की कमी, और स्कूलों में उच्च ड्रॉपआउट दर।

      हमारे एकीकृत विकास दृष्टिकोण ने तीन प्रमुख क्षेत्रों पर ध्यान केंद्रित किया:
      1. जल अवसंरचना
         • जल शुद्धिकरण प्रणाली की स्थापना
         • जल भंडारण टैंकों का निर्माण
         • नियमित जल गुणवत्ता परीक्षण कार्यक्रम
         • स्थानीय युवाओं को प्रणाली रखरखाव में प्रशिक्षण

      2. स्वच्छता सुविधाएं
         • 150 घरेलू शौचालयों का निर्माण
         • स्वच्छता पर सामुदायिक जागरूकता कार्यक्रम
         • अपशिष्ट प्रबंधन प्रणाली कार्यान्वयन
         • नियमित सफाई अभियान

      3. शिक्षा सहायता
         • प्राथमिक विद्यालय भवन का नवीनीकरण
         • स्कूल में स्वच्छ पेयजल की स्थापना
         • लड़कों और लड़कियों के लिए अलग शौचालय सुविधाएं
         • शिक्षक प्रशिक्षण कार्यक्रम

      परिणाम उल्लेखनीय रहे हैं:
      • जल जनित बीमारियों में 70% की कमी
      • 6-14 वर्ष के बच्चों का 100% स्कूल नामांकन
      • प्राथमिक विद्यालय में शून्य ड्रॉपआउट दर
      • खुले में शौच में 90% की कमी
      • विशेष रूप से छात्राओं में उपस्थिति में सुधार

      गांव के प्रधान रमेश कुमार बताते हैं, "परिवर्तन हमारे जीवन के हर पहलू में दिखाई देता है। हमारे बच्चे स्वस्थ हैं और नियमित रूप से स्कूल जा रहे हैं। महिलाओं को पानी के लिए किलोमीटर पैदल नहीं चलना पड़ता।"

      इस परियोजना की सफलता ने रामपुर को एक मॉडल गांव बना दिया है, जिससे पड़ोसी गांव अब इसी तरह के हस्तक्षेप की मांग कर रहे हैं। यह परिवर्तन दिखाता है कि कैसे बुनियादी ढांचागत जरूरतों को पूरा करने से स्वास्थ्य, शिक्षा और समग्र जीवन की गुणवत्ता में सुधार हो सकता है।

      परियोजना की स्थिरता इनके माध्यम से सुनिश्चित की जाती है:
      • रखरखाव के लिए स्थानीय समिति
      • नियमित निगरानी और समर्थन
      • सुविधाओं का सामुदायिक स्वामित्व
      • निरंतर शिक्षा और जागरूकता कार्यक्रम

      यह इस बात का प्रमाण है कि जब समुदाय और विकास संगठन स्पष्ट दृष्टि और निरंतर प्रयास के साथ मिलकर काम करते हैं तो क्या हासिल किया जा सकता है।`
    }
  ]
};

const SuccessStories: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];
  const currentStories = stories[language];

  // Only show the first 3 stories in the preview section
  const previewStories = currentStories.slice(0, 3);

  return (
    <section className="section-padding">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-baloo font-bold text-gray-800 mb-4">
            {t.title}
          </h2>
          <p className="text-gray-600 font-hindi max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {previewStories.map((story, index) => (
            <StoryCard
              key={story.name}
              {...story}
              delay={index * 0.1}
            />
          ))}
        </div>
        
        <div className="text-center mt-12">
          <Link to="/stories" className="btn-primary font-hindi">
            {t.readMore}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SuccessStories; 